import { Grid } from '@mui/material';
import { HSpacer, RoutalAvatar, RoutalPalette, RoutalSelect, VSpacer } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import More3Points from '../../assets/svg/More3Points';
import { HeaderContent } from '../../components/HeaderContent';
import { ContextualMenu } from '../../components/menus/ContextualMenu';
import * as routing from '../../constants/Routing';
import { Project } from '../../domain/project/Project';
import { ProjectUser } from '../../domain/project/ProjectUser';
import { useRetrieveOrganizationUsers } from '../../hooks/organization/useRetrieveOrganizationUsers';
import useRemoveProjectUser from '../../hooks/project/useDeleteProjectUser';
import { useRetrieveProjectUsers } from '../../hooks/project/useRetrieveProjectUsers';
import useUpdateProjectUser from '../../hooks/project/useUpdateProjectUser';
import { useRetrieveSupportDocs } from '../../hooks/support-docs/useRetrieveSupportDocs';
import { useRetrieveUser } from '../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../hooks/user/useRetrieveUserCredentials';
import Button from '../../react-components/Button';
import FormattedMessage from '../../react-components/FormattedCustomMessage';
import Text from '../../react-components/Text';
import { DataTable } from '../../react-components/data-table/DataTable';
import { getUploadAsBase64 } from '../../services/requests';
import { OrganizationNewUserDialog } from '../settings/organization/OrganizationNewUserDialog';
import { OrganizationProjectAddUserDialog } from '../settings/projects/OrganizationProjectAddUserDialog';

const sortUsers = (users: ProjectUser[]): ProjectUser[] => {
  try {
    return users.sort((a: ProjectUser, b: ProjectUser) => {
      const fullNameA = `${a.first_name} ${a.last_name}`;
      const fullNameB = `${b.first_name} ${b.last_name}`;
      // Compare the 2 fullNames
      if (fullNameA < fullNameB) return -1;
      if (fullNameA > fullNameB) return 1;
      return 0;
    });
  } catch (_) {
    return [];
  }
};

type ProjectSettingsMembersProps = {
  activeProject: Project;
};

export const ProjectSettingsMembers = ({ activeProject }: ProjectSettingsMembersProps) => {
  const navigate = useNavigate();
  const { retrieveDoc } = useRetrieveSupportDocs();
  const { data: organizationUsers } = useRetrieveOrganizationUsers();
  const { data: user } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: projectUsers } = useRetrieveProjectUsers({ project: activeProject });
  const { run: deleteProjectUser } = useRemoveProjectUser({ project: activeProject });
  const { run: editProjectUser } = useUpdateProjectUser({ project: activeProject });
  const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [tableContextualAnchor, setTableContextualAnchor] = useState<
    { screenPosition: { top: number; left: number }; projectUser: ProjectUser } | undefined
  >(undefined);

  const getAvailableUsers = () => {
    return (organizationUsers ?? []).filter(
      (organizationUser) => !(projectUsers ?? []).find((_user: ProjectUser) => _user.id === organizationUser.id)
    );
  };

  if (!activeProject || !projectUsers) return null;

  const canManageProjectUsers =
    userCredentials?.planner.projects.test(activeProject.id, `can_create_and_delete_users`) ?? false;

  return (
    <div style={{ padding: `16px 22px` }}>
      <Grid container direction="column">
        <Grid container direction="column" style={{ padding: `16px 22px` }}>
          <Grid item container direction="row" justifyContent="space-between" alignItems="center" wrap="nowrap">
            <HeaderContent
              titleIntlId="projects.settings.members.title"
              article={{
                intlId: `projects.settings.members.article`,
                link: retrieveDoc({ supportPage: `settingsMembers`, language: user?.language.value }),
              }}
            />

            {canManageProjectUsers && (
              <Grid item container wrap="nowrap" justifyContent="flex-end">
                <Grid item>
                  <Button
                    styleType="secondary"
                    onClick={() => setNewUserDialogOpen(true)}
                    intlId="projects.settings.members.buttons.create"
                  />
                </Grid>

                <HSpacer small />

                <Grid item>
                  <Button
                    styleType="primary"
                    disabled={getAvailableUsers().length === 0}
                    onClick={() => setAddUserDialogOpen(true)}
                    intlId="projects.settings.members.buttons.add"
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction="column"
        style={{
          padding: `16px 22px`,
        }}
        wrap="nowrap"
      >
        <Text
          variant="body-large"
          intlId="projects.settings.members.total"
          intlValues={{ total: projectUsers.length }}
        />

        {canManageProjectUsers ? (
          <>
            <VSpacer medium />
            <Grid item>
              <span style={{ fontSize: `13px` }}>
                <FormattedMessage
                  tagName="span"
                  id="projects.settings.members.info"
                  values={{
                    a: (chunks: any) => (
                      <span
                        style={{
                          cursor: `pointer`,
                          color: RoutalPalette.primary40,
                        }}
                        onClick={() => navigate(`${routing.SETTINGS.ORGANIZATION.USERS}`)}
                      >
                        {chunks}
                      </span>
                    ),
                  }}
                />
              </span>
            </Grid>
          </>
        ) : null}

        <VSpacer large />

        <DataTable
          noFooter
          docs={sortUsers(projectUsers).map((user) => ({ doc: user }))}
          total={projectUsers.length}
          showCheckboxes={false}
          handleOnContextMenu={(ev: any, member: ProjectUser) => {
            ev.preventDefault();
            if (!member.deleted && canManageProjectUsers) {
              setTableContextualAnchor({ screenPosition: { top: ev.clientY, left: ev.clientX }, projectUser: member });
            }
          }}
          columns={[
            {
              width: `calc(100% - 400px)`,
              accessorName: `name`,
              name: <Text variant="body" intlId="projects.settings.members.name" />,
              formatter: ({ doc: member }: { doc: ProjectUser }) => (
                <Grid container direction="row" wrap="nowrap" justifyContent="center" alignItems="center">
                  <Grid item>
                    <RoutalAvatar
                      url={member.avatar_url}
                      getUploadAsBase64={getUploadAsBase64}
                      label={`${member.first_name}_${member.last_name}`}
                      labelStyles={{ fontSize: `12px`, lineHeight: 2 }}
                      containerStyles={{ width: `32px`, height: `32px` }}
                    />
                  </Grid>

                  <HSpacer medium />

                  <Grid container direction="column" justifyContent="center">
                    <Grid item>
                      <Text
                        variant="body-large"
                        style={{
                          ...(member.deleted ? { textDecoration: `line-through` } : {}),
                          ...(!member.validated ? { color: `#76727B` } : {}),
                        }}
                      >
                        {`${member.first_name} ${member.last_name}`}
                        {!member.validated && !member.deleted && (
                          <Text variant="body" tagName="span">
                            {` `}
                            (
                            <FormattedMessage tagName="span" id="projects.settings.members.pending" />)
                          </Text>
                        )}
                      </Text>
                    </Grid>

                    <VSpacer small />

                    <Grid item>
                      <Text
                        variant="body-large"
                        style={{
                          ...(!member.validated ? { color: `#76727B` } : {}),
                        }}
                      >
                        {member.email}
                      </Text>
                    </Grid>
                  </Grid>
                </Grid>
              ),
            },
            {
              accessorName: `role`,
              name: (
                <Text
                  variant="body"
                  style={{ cursor: `default`, textTransform: `none` }}
                  intlId="projects.settings.members.project_role"
                />
              ),
              formatter: ({ doc: projectUser }: { doc: ProjectUser }) => {
                return (
                  <RoutalSelect
                    id="project-member-role"
                    containerStyle={{
                      width: `250px`,
                    }}
                    disabled={projectUser.deleted || !canManageProjectUsers}
                    popoverProps={{
                      maxHeight: `400px`,
                      options: [
                        {
                          key: `project_manager`,
                          width: 260,
                          listItemStyles: {
                            whiteSpace: `normal` as any,
                            overflow: `auto`,
                            color: `#929599`,
                            height: `100%`,
                            padding: `8px 16px`,
                            ...(projectUser.role === `project_manager` ? { backgroundColor: `#f8671d14` } : {}),
                          },
                          content: (
                            <Grid container direction="column" wrap="nowrap">
                              <Text
                                style={{ cursor: `pointer` }}
                                variant="body-medium"
                                intlId="projects.settings.roles.manager"
                              />
                              <Text
                                style={{ cursor: `pointer` }}
                                variant="body"
                                intlId="projects.settings.roles.manager.description"
                              />
                            </Grid>
                          ),
                          onClick: () => editProjectUser({ projectUser, role: `project_manager` }),
                        },
                        {
                          key: `project_user`,
                          width: 260,
                          listItemStyles: {
                            whiteSpace: `normal` as any,
                            overflow: `auto`,
                            color: `#929599`,
                            height: `100%`,
                            padding: `8px 16px`,
                            ...(projectUser.role === `project_user` ? { backgroundColor: `#f8671d14` } : {}),
                          },
                          content: (
                            <Grid container direction="column" wrap="nowrap">
                              <Text
                                style={{ cursor: `pointer` }}
                                variant="body-medium"
                                intlId="projects.settings.roles.user"
                              />
                              <Text
                                style={{ cursor: `pointer` }}
                                variant="body"
                                intlId="projects.settings.roles.user.description"
                              />
                            </Grid>
                          ),
                          onClick: () => editProjectUser({ projectUser, role: `project_user` }),
                        },
                      ],
                    }}
                  >
                    <Text
                      variant="body"
                      style={{ cursor: `pointer`, width: `100%`, textAlign: `start` }}
                      intlId={`projects.settings.roles.${projectUser.role === `project_manager` ? `manager` : `user`}`}
                    />
                  </RoutalSelect>
                );
              },
            },
            {
              accessorName: `actions`,
              align: `right`,
              formatter: ({ doc: projectUser }: { doc: ProjectUser }) => {
                if (projectUser.deleted || !canManageProjectUsers) return null;

                return (
                  <RoutalSelect
                    id="project-member-actions"
                    hideArrow
                    style={{
                      width: `28px`,
                      height: `28px`,
                      padding: `2px`,
                      margin: `0px`,
                      border: `none`,
                      backgroundColor: `transparent`,
                      color: `#393939`,
                    }}
                    popoverProps={{
                      anchor: {
                        anchorOrigin: {
                          vertical: `bottom`,
                          horizontal: `right`,
                        },
                        transformOrigin: {
                          vertical: `top`,
                          horizontal: `right`,
                        },
                      },
                      options: [
                        {
                          key: `delete`,
                          onClick: () => deleteProjectUser({ projectUser }),
                          content: (
                            <Text
                              intlId="projects.settings.members.buttons.delete"
                              style={{ color: RoutalPalette.danger, cursor: `pointer` }}
                            />
                          ),
                        },
                      ],
                    }}
                  >
                    <More3Points />
                  </RoutalSelect>
                );
              },
            },
          ]}
        />

        {tableContextualAnchor ? (
          <ContextualMenu
            anchorElementPosition={tableContextualAnchor.screenPosition}
            onClose={() => {
              setTableContextualAnchor(undefined);
            }}
            options={[
              {
                key: `delete`,
                text: (
                  <Text
                    intlId="projects.settings.members.buttons.delete"
                    style={{ color: RoutalPalette.danger, cursor: `pointer` }}
                  />
                ),
                onClick: () => {
                  deleteProjectUser({ projectUser: tableContextualAnchor.projectUser });
                },
              },
            ]}
          />
        ) : null}
      </Grid>

      <OrganizationNewUserDialog
        isOpen={newUserDialogOpen}
        selectedProjectId={activeProject.id}
        onClose={() => setNewUserDialogOpen(false)}
      />

      <OrganizationProjectAddUserDialog
        selectedProject={activeProject}
        availableUsers={getAvailableUsers()}
        isOpen={addUserDialogOpen}
        onClose={() => setAddUserDialogOpen(false)}
      />
    </div>
  );
};
